(() => {
  const coordinate = (value, limit) => {
    if (typeof value !== 'string' || !value.trim()) return null;
    const number = Number(value);
    return Number.isFinite(number) && Math.abs(number) <= limit ? number : null;
  };

  const distanceBetween = (from, to, unit) => {
    const radians = (value) => (value * Math.PI) / 180;
    const deltaLat = radians(to.lat - from.lat);
    const deltaLng = radians(to.lng - from.lng);
    const a = Math.sin(deltaLat / 2) ** 2 + Math.cos(radians(from.lat)) * Math.cos(radians(to.lat)) * Math.sin(deltaLng / 2) ** 2;
    const km = 6371.0088 * 2 * Math.asin(Math.sqrt(Math.min(1, Math.max(0, a))));
    return unit === 'miles' ? km / 1.609344 : km;
  };

  const loadMaps = (key, language) => {
    if (window.vestaStoreLocatorAuthFailed) return Promise.reject(new Error('Map authentication failed'));
    if (window.google?.maps?.importLibrary) return Promise.resolve(window.google.maps);
    if (window.vestaStoreLocatorMaps) return window.vestaStoreLocatorMaps;
    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      window.vestaStoreLocatorAuthFailed = true;
      window.dispatchEvent(new Event('vesta:maps-auth-failed'));
      if (typeof previousAuthFailure === 'function') previousAuthFailure();
    };
    window.vestaStoreLocatorMaps = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const callback = 'vestaStoreLocatorMapsLoaded';
      const finish = (error) => {
        clearTimeout(timer);
        if (error) {
          script.remove();
          reject(error);
        } else resolve(window.google.maps);
      };
      const timer = setTimeout(() => finish(new Error('Map load timed out')), 20000);
      window[callback] = () => finish();
      const params = new URLSearchParams({ key, callback, loading: 'async', v: 'quarterly', libraries: 'marker', language });
      script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
      script.async = true;
      script.onerror = () => finish(new Error('Map load failed'));
      document.head.append(script);
    });
    window.vestaStoreLocatorMaps.catch(() => { window.vestaStoreLocatorMaps = null; });
    return window.vestaStoreLocatorMaps;
  };

  class VestaStoreLocator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.operation = 0;
      this.center = null;
      this.selected = null;
      this.textFilter = '';
      this.markers = new Map();
      this.unit = this.dataset.unit === 'km' ? 'km' : 'miles';
      this.list = this.querySelector('[data-list]');
      this.input = this.querySelector('[data-search]');
      this.suggestionsPane = this.querySelector('[data-suggestions]');
      this.suggestionList = this.querySelector('[data-suggestion-list]');
      this.suggestionRequest = 0;
      this.predictions = [];
      this.activeSuggestion = -1;
      this.status = this.querySelector('[data-status]');
      this.detailsPane = this.querySelector('[data-store-overlay]');
      this.directory = this.querySelector('[data-directory]');
      this.stores = Array.from(this.querySelectorAll('[data-store]'), (card) => {
        const lat = coordinate(card.dataset.lat, 90);
        const lng = coordinate(card.dataset.lng, 180);
        return { card, name: card.dataset.name, address: card.dataset.address, position: lat === null || lng === null ? null : { lat, lng }, distance: null };
      });
      const listen = (element, type, handler) => element.addEventListener(type, handler, { signal: this.controller.signal });
      listen(window, 'vesta:maps-auth-failed', () => {
        this.cancelMapFit();
        this.mapFailed = true;
        this.mapPromise = Promise.resolve(false);
        this.markers.forEach((marker) => { marker.map = null; });
        this.closeDetails();
        this.map = null;
        this.mapUnavailable();
      });
      this.querySelector('[data-search-form]').hidden = false;
      listen(this.querySelector('[data-search-form]'), 'submit', (event) => { event.preventDefault(); this.search(); });
      listen(this.input, 'input', (event) => {
        this.operation += 1;
        this.querySelector('.sl-search-submit').disabled = false;
        this.querySelector('[data-locate]').disabled = false;
        this.message();
        if (!this.input.value.trim()) this.reset();
        else if (!event.isComposing) this.queueSuggestions();
      });
      listen(this.input, 'compositionstart', () => this.closeSuggestions());
      listen(this.input, 'compositionend', () => this.queueSuggestions());
      listen(this.input, 'keydown', (event) => this.suggestionKeydown(event));
      listen(this.input, 'focus', () => this.queueSuggestions());
      listen(this.input, 'blur', () => this.closeSuggestions());
      listen(this.suggestionList, 'pointerdown', (event) => {
        if (event.target.closest('[data-suggestion-index]')) event.preventDefault();
      });
      listen(this.suggestionList, 'click', (event) => {
        const button = event.target.closest('[data-suggestion-index]');
        if (button) this.chooseSuggestion(this.predictions[Number(button.dataset.suggestionIndex)]);
      });
      listen(this.querySelector('[data-locate]'), 'click', () => this.locate());
      listen(this.querySelector('[data-recenter]'), 'click', () => { this.closeSuggestions(); this.requestMapFit(); });
      this.querySelectorAll('[data-zoom]').forEach((button) => listen(button, 'click', () => {
        if (!this.map) return;
        this.cancelMapFit();
        this.closeSuggestions();
        this.map.setZoom(Math.max(0, Math.min(22, this.map.getZoom() + Number(button.dataset.zoom))));
      }));
      this.querySelectorAll('[data-reset], [data-empty-reset]').forEach((button) => listen(button, 'click', () => this.reset()));
      listen(this.querySelector('[data-detail-close]'), 'click', () => this.closeDetails({ focusMarker: true }));
      listen(this.detailsPane, 'keydown', (event) => {
        if (event.key === 'Escape') { event.preventDefault(); this.closeDetails({ focusMarker: true }); }
      });
      this.stores.forEach((store) => {
        listen(store.card.querySelector('[data-select]'), 'click', () => {
          if (this.map && store.position) {
            this.cancelMapFit();
            this.cameraFrame = requestAnimationFrame(() => {
              this.cameraFrame = null;
              if (this.map && this.isConnected) this.select(store);
            });
          } else this.select(store);
        });
      });
      this.render();
      if (!this.dataset.apiKey || !this.dataset.mapId || !this.stores.some((store) => store.position)) {
        this.mapUnavailable(this.stores.some((store) => store.position) ? this.dataset.mapError : this.dataset.mapEmpty);
        return;
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.observer.disconnect();
            this.ensureMap();
          }
        }, { rootMargin: '200px' });
        this.observer.observe(this);
      } else this.ensureMap();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.cancelMapFit();
      this.closeSuggestions();
      this.autocompleteSession = null;
      this.operation += 1;
      this.closeDetails();
      this.markers?.forEach((marker) => { marker.map = null; });
      this.userMarker && (this.userMarker.map = null);
      this.map = null;
      this.mapPromise = null;
      this.initialized = false;
    }

    message(value = '') {
      this.status.textContent = value;
      this.status.hidden = !value;
    }

    mapUnavailable(message = this.dataset.mapError) {
      this.querySelector('[data-map-controls]').hidden = true;
      this.querySelector('[data-map]').hidden = true;
      this.querySelector('[data-map-message]').textContent = message;
      this.querySelector('[data-map-placeholder]').hidden = false;
      this.closeDetails();
      this.directory.hidden = false;
    }

    async ensureMap() {
      if (this.mapPromise) return this.mapPromise;
      if (!this.dataset.apiKey || !this.dataset.mapId || !this.stores.some((store) => store.position)) return false;
      this.mapPromise = (async () => {
        try {
          const maps = await loadMaps(this.dataset.apiKey, this.dataset.language);
          const [{ Map }, { AdvancedMarkerElement, PinElement }] = await Promise.all([maps.importLibrary('maps'), maps.importLibrary('marker')]);
          if (!this.isConnected || this.mapFailed) return false;
          this.maps = maps;
          this.Marker = AdvancedMarkerElement;
          this.Pin = PinElement;
          this.map = new Map(this.querySelector('[data-map]'), {
            mapId: this.dataset.mapId,
            center: this.stores.find((store) => store.position).position,
            zoom: 9,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            cameraControl: false,
            zoomControl: false,
            gestureHandling: 'cooperative'
          });
          // A user gesture takes ownership of the camera, including pending adjustments.
          ['pointerdown', 'wheel', 'keydown'].forEach((type) => {
            this.map.getDiv().addEventListener(type, () => this.cancelMapFit(), { capture: true, passive: true, signal: this.controller.signal });
          });
          this.stores.filter((store) => store.position).forEach((store) => {
            const pin = document.createElement('div');
            pin.className = 'sl-marker';
            pin.innerHTML = '<svg viewBox="0 0 40 52" aria-hidden="true"><path class="sl-marker__shape" d="M20 1C9.5 1 2 8.8 2 19c0 13 18 31 18 31s18-18 18-31C38 8.8 30.5 1 20 1Z"/><path fill="white" d="m9 12 8 17h6l8-17h-6l-5 11-5-11Z"/></svg>';
            store.pin = pin;
            const marker = new AdvancedMarkerElement({ map: this.map, position: store.position, title: store.name, gmpClickable: true, zIndex: 0 });
            marker.append(pin);
            marker.addEventListener('gmp-click', () => { this.cancelMapFit(); this.select(store); }, { signal: this.controller.signal });
            this.markers.set(store, marker);
          });
          this.querySelector('[data-map-placeholder]').hidden = true;
          this.querySelector('[data-map]').hidden = false;
          this.directory.hidden = true;
          this.querySelector('[data-map-controls]').hidden = false;
          this.render();
          this.requestMapFit();
          return true;
        } catch {
          if (this.isConnected) this.mapUnavailable();
          return false;
        }
      })();
      return this.mapPromise;
    }

    cancelMapFit() {
      cancelAnimationFrame(this.cameraFrame);
      this.cameraFrame = null;
      this.zoomLimitListener?.remove();
      this.zoomLimitListener = null;
      this.needsMapFit = false;
    }

    requestMapFit() {
      this.cancelMapFit();
      this.needsMapFit = true;
      this.cameraFrame = requestAnimationFrame(() => {
        this.cameraFrame = null;
        if (!this.map || !this.isConnected) return;
        const { clientWidth, clientHeight } = this.map.getDiv();
        // Defer camera fitting while the section has no visible dimensions.
        if (!clientWidth || !clientHeight) return;
        this.needsMapFit = false;
        this.fitMap(this.visibleStores);
      });
    }

    updateCenter(center, label) {
      this.closeSuggestions();
      this.center = center;
      this.textFilter = '';
      this.input.value = label;
      this.message();
      this.closeDetails();
      this.render();
      this.requestMapFit();
      const nearest = this.visibleStores.find((store) => store.position);
      if (nearest) this.select(nearest, { pan: false });
    }

    closeSuggestions() {
      clearTimeout(this.suggestionTimer);
      this.suggestionRequest += 1;
      this.predictions = [];
      this.activeSuggestion = -1;
      this.suggestionList?.replaceChildren();
      if (this.suggestionsPane) this.suggestionsPane.hidden = true;
      this.input?.setAttribute('aria-expanded', 'false');
      this.input?.removeAttribute('aria-activedescendant');
    }

    queueSuggestions() {
      this.closeSuggestions();
      const query = this.input.value.trim();
      if (query.length < 3 || !this.dataset.apiKey) return;
      const requestId = this.suggestionRequest;
      this.suggestionTimer = setTimeout(() => this.fetchSuggestions(query, requestId), 300);
    }

    async fetchSuggestions(query, requestId) {
      const current = () => requestId === this.suggestionRequest && this.isConnected && this.input.value.trim() === query && document.activeElement === this.input;
      try {
        if (!await this.ensureMap() || !current()) return;
        const { AutocompleteSuggestion, AutocompleteSessionToken } = await this.maps.importLibrary('places');
        if (!current()) return;
        if (!this.autocompleteSession || Date.now() - this.autocompleteStarted > 120000) {
          this.autocompleteSession = new AutocompleteSessionToken();
          this.autocompleteStarted = Date.now();
        }
        const request = { input: query, language: this.dataset.language, sessionToken: this.autocompleteSession };
        if (/^[A-Z]{2}$/.test(this.dataset.country)) request.includedRegionCodes = [this.dataset.country.toLowerCase()];
        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        if (!current()) return;
        this.predictions = suggestions.map((suggestion) => suggestion.placePrediction).filter(Boolean).slice(0, 5);
        this.predictions.forEach((prediction, index) => {
          const item = document.createElement('li');
          item.setAttribute('role', 'presentation');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'sl-suggestion';
          button.id = `${this.suggestionList.id}-${index}`;
          button.dataset.suggestionIndex = index;
          button.tabIndex = -1;
          button.setAttribute('role', 'option');
          button.setAttribute('aria-selected', 'false');
          const main = document.createElement('strong');
          main.textContent = prediction.mainText?.toString() || prediction.text.toString();
          button.append(main);
          if (prediction.secondaryText) {
            const secondary = document.createElement('span');
            secondary.textContent = prediction.secondaryText.toString();
            button.append(secondary);
          }
          item.append(button);
          this.suggestionList.append(item);
        });
        this.suggestionsPane.hidden = !this.predictions.length;
        this.input.setAttribute('aria-expanded', String(Boolean(this.predictions.length)));
      } catch {
        if (current()) this.closeSuggestions();
      }
    }

    suggestionKeydown(event) {
      if (event.isComposing || this.suggestionsPane.hidden) return;
      if (event.key === 'Escape') { event.preventDefault(); this.closeSuggestions(); return; }
      if (event.key === 'Enter' && this.activeSuggestion >= 0) {
        event.preventDefault();
        this.chooseSuggestion(this.predictions[this.activeSuggestion]);
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      const count = this.predictions.length;
      this.activeSuggestion = this.activeSuggestion < 0 ? (event.key === 'ArrowDown' ? 0 : count - 1) : (this.activeSuggestion + (event.key === 'ArrowDown' ? 1 : -1) + count) % count;
      this.suggestionList.querySelectorAll('[role="option"]').forEach((option, index) => {
        const selected = index === this.activeSuggestion;
        option.setAttribute('aria-selected', String(selected));
        if (selected) {
          this.input.setAttribute('aria-activedescendant', option.id);
          option.scrollIntoView({ block: 'nearest' });
        }
      });
    }

    async chooseSuggestion(prediction) {
      if (!prediction) return;
      const label = prediction.text.toString();
      const place = prediction.toPlace();
      this.closeSuggestions();
      this.autocompleteSession = null;
      this.input.value = label;
      const operation = ++this.operation;
      this.message(this.dataset.searching);
      this.querySelector('[data-locate]').disabled = false;
      this.querySelector('.sl-search-submit').disabled = true;
      try {
        await place.fetchFields({ fields: ['location'] });
        if (operation !== this.operation || !this.isConnected) return;
        if (!place.location) throw new Error('No place location');
        this.updateCenter({ lat: place.location.lat(), lng: place.location.lng() }, label);
      } catch {
        if (operation === this.operation && this.isConnected) this.message(this.dataset.searchError);
      } finally {
        if (operation === this.operation && this.isConnected) this.querySelector('.sl-search-submit').disabled = false;
      }
    }

    async search() {
      this.closeSuggestions();
      this.autocompleteSession = null;
      const query = this.input.value.trim();
      if (!query) return this.reset();
      const operation = ++this.operation;
      this.message(this.dataset.searching);
      this.querySelector('[data-locate]').disabled = false;
      this.querySelector('.sl-search-submit').disabled = true;
      try {
        const ready = await this.ensureMap();
        if (operation !== this.operation || !this.isConnected) return;
        if (!ready) {
          this.center = null;
          this.textFilter = query.toLocaleLowerCase();
          this.message(this.dataset.mapError);
          this.render();
          return;
        }
        const { Geocoder } = await this.maps.importLibrary('geocoding');
        const request = { address: query };
        if (/^[A-Z]{2}$/.test(this.dataset.country)) request.componentRestrictions = { country: this.dataset.country };
        const response = await new Geocoder().geocode(request);
        if (operation !== this.operation || !this.isConnected) return;
        const result = response.results[0];
        if (!result) throw new Error('No search results');
        this.updateCenter({ lat: result.geometry.location.lat(), lng: result.geometry.location.lng() }, query);
      } catch {
        if (operation === this.operation && this.isConnected) this.message(this.dataset.searchError);
      } finally {
        if (operation === this.operation && this.isConnected) this.querySelector('.sl-search-submit').disabled = false;
      }
    }

    locate() {
      this.closeSuggestions();
      this.autocompleteSession = null;
      if (!navigator.geolocation) return this.message(this.dataset.locationError);
      const operation = ++this.operation;
      const button = this.querySelector('[data-locate]');
      button.disabled = true;
      this.querySelector('.sl-search-submit').disabled = false;
      this.message(this.dataset.locating);
      navigator.geolocation.getCurrentPosition((position) => {
        if (operation !== this.operation || !this.isConnected) return;
        button.disabled = false;
        this.updateCenter({ lat: position.coords.latitude, lng: position.coords.longitude }, '');
      }, () => {
        if (operation !== this.operation || !this.isConnected) return;
        button.disabled = false;
        this.message(this.dataset.locationError);
      }, { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 });
    }

    reset() {
      this.closeSuggestions();
      this.autocompleteSession = null;
      this.operation += 1;
      this.center = null;
      this.textFilter = '';
      this.input.value = '';
      this.querySelector('[data-locate]').disabled = false;
      this.querySelector('.sl-search-submit').disabled = false;
      this.message();
      this.closeDetails();
      this.render();
      this.requestMapFit();
    }

    render() {
      const ordered = [...this.stores];
      ordered.forEach((store) => {
        store.distance = this.center && store.position ? distanceBetween(this.center, store.position, this.unit) : null;
        const matchesText = !this.textFilter || `${store.name} ${store.address}`.toLocaleLowerCase().includes(this.textFilter);
        store.card.hidden = !matchesText;
        const distance = store.card.querySelector('[data-distance]');
        distance.hidden = store.distance === null;
        if (store.distance !== null) distance.textContent = this.dataset.distanceTemplate.replace('[distance]', store.distance.toLocaleString(this.dataset.language, { maximumFractionDigits: 1 })).replace('[unit]', this.unit);
        const marker = this.markers.get(store);
        if (marker) marker.map = store.card.hidden ? null : this.map;
      });
      ordered.sort((left, right) => this.center ? (left.distance ?? Infinity) - (right.distance ?? Infinity) || left.name.localeCompare(right.name) : left.name.localeCompare(right.name));
      ordered.forEach((store) => this.list.append(store.card));
      this.visibleStores = ordered.filter((store) => !store.card.hidden);
      this.querySelector('[data-empty]').hidden = Boolean(this.visibleStores.length);
      this.querySelector('[data-empty-text]').hidden = Boolean(this.stores.length);
      this.querySelector('[data-no-results]').hidden = !this.stores.length;
      const filtered = Boolean(this.center || this.textFilter);
      this.querySelector('[data-reset]').hidden = !filtered;
      this.querySelector('[data-empty-reset]').hidden = !filtered;
      if (this.selected?.card.hidden) this.closeDetails();
      if (this.map) {
        if (this.userMarker) this.userMarker.map = null;
        if (this.center) {
          this.userMarker = new this.Marker({ map: this.map, position: this.center, title: this.input.value || this.querySelector('[data-locate]').textContent.trim() });
          this.userMarker.append(new this.Pin({ background: '#4285f4', borderColor: '#ffffff', glyphColor: '#ffffff', scale: .8 }));
        }
      }
    }

    mapInsets() {
      const insets = { top: 50, right: 50, bottom: 50, left: 50 };
      const map = this.map.getDiv().getBoundingClientRect();
      const toolbar = this.querySelector('[data-search-form]').getBoundingClientRect();
      insets.top = Math.max(50, toolbar.bottom - map.top + 40);
      if (!this.detailsPane.hidden && getComputedStyle(this.detailsPane).position === 'absolute') {
        const card = this.detailsPane.getBoundingClientRect();
        if (card.top > map.top + map.height / 2) insets.bottom = Math.max(50, map.bottom - card.top + 24);
        else insets.left = Math.max(50, card.right - map.left + 50);
      }
      return insets;
    }

    fitMap(stores) {
      if (!this.map || !stores) return;
      const locatedStores = stores.filter((store) => store.position);
      if (this.center) {
        this.map.setHeading(0);
        this.map.setTilt(0);
        this.map.setCenter(this.center);
        const nearest = locatedStores.reduce((closest, store) => !closest || store.distance < closest.distance ? store : closest, null);
        if (!nearest || distanceBetween(this.center, nearest.position, 'km') < .05) {
          this.map.setZoom(14);
          return;
        }
        const { clientWidth: width, clientHeight: height } = this.map.getDiv();
        if (!width || !height) return;
        const project = ({ lat, lng }) => {
          const latitude = Math.max(-85.05112878, Math.min(85.05112878, lat)) * Math.PI / 180;
          return { x: (lng + 180) / 360, y: (1 - Math.log(Math.tan(Math.PI / 4 + latitude / 2)) / Math.PI) / 2 };
        };
        const origin = project(this.center);
        const destination = project(nearest.position);
        const longitudeDelta = ((destination.x - origin.x + 1.5) % 1) - .5;
        const x = Math.abs(longitudeDelta);
        const latitudeDelta = destination.y - origin.y;
        const y = Math.abs(latitudeDelta);
        const insets = this.mapInsets();
        const horizontalSpace = width / 2 - (longitudeDelta < 0 ? insets.left : insets.right);
        const zoomX = x ? Math.log2(Math.max(1, horizontalSpace) / (256 * x)) : Infinity;
        const verticalSpace = height / 2 - (latitudeDelta < 0 ? insets.top : insets.bottom);
        const zoomY = y ? Math.log2(Math.max(1, verticalSpace) / (256 * y)) : Infinity;
        this.map.setZoom(Math.max(0, Math.min(16, zoomX, zoomY)));
        return;
      }
      const positions = locatedStores.map((store) => store.position);
      if (positions.length === 1) { this.map.setCenter(positions[0]); this.map.setZoom(14); }
      else if (positions.length > 1) {
        const bounds = new this.maps.LatLngBounds();
        positions.forEach((position) => bounds.extend(position));
        const map = this.map;
        this.zoomLimitListener = this.maps.event.addListenerOnce(map, 'idle', () => {
          this.zoomLimitListener = null;
          if (this.map === map && map.getZoom() > 16) map.setZoom(16);
        });
        this.map.fitBounds(bounds, this.mapInsets());
      }
    }

    closeDetails({ focusMarker = false } = {}) {
      if (this.selected) {
        this.selected.card.classList.remove('is-selected');
        this.selected.card.querySelector('[data-select]').setAttribute('aria-pressed', 'false');
        this.selected.pin?.classList.remove('is-selected');
        const marker = this.markers.get(this.selected);
        if (marker) { marker.zIndex = 0; if (focusMarker) marker.focus(); }
      }
      this.selected = null;
      if (this.detailsPane) this.detailsPane.hidden = true;
      const announcement = this.querySelector('[data-selected-announcement]');
      if (announcement) announcement.textContent = '';
    }

    select(store, { pan = true } = {}) {
      this.closeDetails();
      this.selected = store;
      store.card.classList.add('is-selected');
      store.card.querySelector('[data-select]').setAttribute('aria-pressed', 'true');
      store.pin?.classList.add('is-selected');
      const marker = this.markers.get(store);
      if (!marker) return;
      marker.zIndex = 1;
      const details = store.card.querySelector('[data-detail-content]').cloneNode(true);
      const heading = document.createElement('h2');
      heading.id = this.detailsPane.getAttribute('aria-labelledby');
      heading.textContent = store.name;
      details.querySelector('.sl-popup-badge').after(heading);
      this.querySelector('[data-overlay-content]').replaceChildren(details);
      this.detailsPane.hidden = false;
      this.querySelector('[data-selected-announcement]').textContent = store.name;
      if (pan) this.map.panTo(store.position);
    }

  }

  if (!customElements.get('vesta-store-locator')) customElements.define('vesta-store-locator', VestaStoreLocator);
})();
